import React, { memo, useEffect, useMemo, useState } from "react"
import styles from "./emojis.module.css"
import { getWhatsAppEmojiUrl } from "../../utils/utils"

const loadedEmojiImages = new Set()
const loadedSpriteImages = new Set()
const loadingSpriteImages = new Map()

const preloadEmojiImage = (src) => {
    if (!src || loadedEmojiImages.has(src)) return

    loadedEmojiImages.add(src)
    const image = new Image()
    image.decoding = "async"
    image.src = src
}

const loadSpriteImage = (src) => {
    if (loadedSpriteImages.has(src)) return Promise.resolve()
    if (loadingSpriteImages.has(src)) return loadingSpriteImages.get(src)

    const promise = new Promise((resolve, reject) => {
        const image = new Image()
        image.decoding = "async"
        image.onload = () => {
            loadedSpriteImages.add(src)
            loadingSpriteImages.delete(src)
            resolve()
        }
        image.onerror = () => {
            loadingSpriteImages.delete(src)
            reject(new Error(`Falha ao carregar sprite ${src}`))
        }
        image.src = src
    })

    loadingSpriteImages.set(src, promise)
    return promise
}

const Emoji = ({ icon, url, sprite, onClick = () => { } }) => {
    const src = useMemo(() => url || getWhatsAppEmojiUrl(icon), [icon, url])
    const spriteUrl = sprite?.url || ""
    const [spriteLoaded, setSpriteLoaded] = useState(() => spriteUrl ? loadedSpriteImages.has(spriteUrl) : false)

    useEffect(() => {
        if (!spriteUrl) return

        let mounted = true
        setSpriteLoaded(loadedSpriteImages.has(spriteUrl))
        loadSpriteImage(spriteUrl)
            .then(() => mounted && setSpriteLoaded(true))
            .catch(() => mounted && setSpriteLoaded(false))

        return () => {
            mounted = false
        }
    }, [spriteUrl])

    return (
        <button
            type="button"
            className={styles.icon}
            onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onClick(icon)
            }}
            onMouseEnter={() => !sprite && preloadEmojiImage(src)}
            aria-label={`Emoji ${icon}`}
        >
            {sprite ? (
                <>
                    {spriteLoaded && (
                        <span
                            className={styles.sprite}
                            aria-hidden="true"
                            style={{
                                backgroundImage: `url("${spriteUrl}")`,
                                backgroundPosition: `-${sprite.x}px -${sprite.y}px`,
                                backgroundSize: `${sprite.width}px ${sprite.height}px`,
                            }}
                        />
                    )}
                    <span style={{ display: spriteLoaded ? "none" : "inline" }}>{icon}</span>
                </>
            ) : (
                <>
                    <img
                        src={src}
                        alt={icon}
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                            e.currentTarget.style.display = "none"
                            if (e.currentTarget.nextSibling) {
                                e.currentTarget.nextSibling.style.display = "inline"
                            }
                        }}
                    />
                    <span>{icon}</span>
                </>
            )}
        </button>
    )
}

export default memo(Emoji)
